import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectTerritorySummaries } from '@user/store/territory'
import { selectAllCampaigns } from '@user/store/campaigns'
import { selectAllDivisions } from '@user/store/divisions'
import { selectAllLocationTypes } from '@user/store/locationTypes'
import { selectAllSources } from '@user/store/sources'
import { selectAllUsers } from '@user/store/users'
import {
  selectAllStates,
  selectAllStatesProvinces,
} from '@user/store/dictionaries'
import { selectAllTargetPests } from '@user/store/targetPests'
import { selectAllBranches } from '@user/store/branches'
import type { LeadAttribute } from '@user/components/Settings/LeadAssignmentRule/LeadAssignmentRuleForm/typings'
import {
  leadTypeImportOptionAssignment,
  leadTypeOptionsLeadAssignment,
  leadTypeServiceBotOptionAssignment,
} from '@user/components/Leads/LeadFilters/typings'
import { useServiceBotEnabled } from '@user/store/tenantFeatures/hooks/useServiceBotEnabled'

export function useGetOptions() {
  const campaigns = useSelector(selectAllCampaigns)
  const branches = useSelector(selectAllBranches)
  const divisions = useSelector(selectAllDivisions)
  const locationTypes = useSelector(selectAllLocationTypes)
  const leadSources = useSelector(selectAllSources)
  const users = useSelector(selectAllUsers)
  const states = useSelector(selectAllStates)
  const allStatesProvinces = useSelector(selectAllStatesProvinces)
  const targetPests = useSelector(selectAllTargetPests)

  const enabledServiceBot = useServiceBotEnabled()
  const territorySummaries = useSelector(selectTerritorySummaries)

  // Ids as STRINGS, deliberately. Condition values are stored and returned as strings, and the
  // edit form rehydrates a saved condition by matching option.id against the stored value -- a
  // numeric id would never match it, and the saved territories would come back blank.
  const territories = useMemo(
    () =>
      territorySummaries.map((territory) => ({
        id: String(territory.id),
        name: territory.name,
      })),
    [territorySummaries],
  )

  let leadTypeData = leadTypeOptionsLeadAssignment ?? []

  if (enabledServiceBot) {
    leadTypeData = [...leadTypeData, ...leadTypeServiceBotOptionAssignment]
  }

  leadTypeData = [...leadTypeData, ...leadTypeImportOptionAssignment]

  const getOptions = useCallback(
    (attribute: LeadAttribute) => {
      switch (attribute) {
        case 'Campaign':
          return campaigns
        case 'Branch':
          return branches
        case 'Division':
          return divisions
        case 'LocationType':
          return locationTypes
        case 'LeadSource':
          return leadSources
        case 'CreatedBy':
          return users
        case 'State':
          return states
        case 'StateAll':
          return allStatesProvinces
        case 'TargetPest':
          return targetPests
        case 'LeadType':
          return leadTypeData
        case 'Territory':
          return territories
        default:
          return []
      }
    },
    [
      campaigns,
      branches,
      divisions,
      locationTypes,
      leadSources,
      users,
      states,
      allStatesProvinces,
      targetPests,
      territories,
    ],
  )

  return getOptions
}
